import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './AppIcon';
import { useTheme } from '../contexts/ThemeContext';

const WhatsAppButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { isDark } = useTheme();
  
  // Your WhatsApp number (format: country code + number without + or spaces)
  const whatsappNumber = '94712435202'; // +94 71 243 5202
  const message = 'Hello! I am interested in discussing a project with you.';

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const handleWhatsAppClick = () => {
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8, y: 20, rotateZ: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0, rotateZ: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20, rotateZ: -10 }}
          whileHover={{ scale: 1.1, rotateZ: -5, y: -4 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleWhatsAppClick}
          className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-50 w-16 h-16 rounded-xl shadow-2xl transition-all duration-300 hover:shadow-green-500/50 bg-gradient-to-br from-green-500 via-green-600 to-emerald-700 text-white flex items-center justify-center group overflow-hidden cursor-pointer"
          style={{
            boxShadow: '0 8px 32px rgba(34, 197, 94, 0.4), inset 0 -4px 8px rgba(0, 0, 0, 0.2), inset 0 4px 8px rgba(255, 255, 255, 0.1)'
          }}
          aria-label="Chat on WhatsApp"
        >
          {/* Animated gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-shimmer"></div>
          
          {/* Pulsing background */}
          <div className="absolute inset-0 bg-green-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300 animate-pulse"></div>
          
          {/* 3D border effect */}
          <div className="absolute inset-0 rounded-xl border-2 border-white/20"></div>
          <div className="absolute inset-1 rounded-lg border border-white/10"></div>
          
          {/* Icon container */}
          <div className="relative z-10 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-8 h-8 group-hover:scale-110 transition-transform duration-300 drop-shadow-lg"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
          </div>

          {/* Tooltip */}
          <div className="absolute right-full mr-4 whitespace-nowrap bg-gradient-to-r from-gray-900 to-gray-800 text-white text-xs font-semibold px-4 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-xl">
            Chat on WhatsApp
            <div className="absolute top-1/2 -right-1.5 transform -translate-y-1/2 w-3 h-3 bg-gray-900 rotate-45"></div>
          </div>

          {/* Corner accent */}
          <div className="absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-sm"></div>
        </motion.button>
      )}
    </AnimatePresence>
  );
};

export default WhatsAppButton;
